/**
 * 上傳前驗證與壓縮（商品列表、物品定義等共用）
 * 選圖後自動壓縮到 ≤2MB，無需使用者手動壓圖。
 */

const ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp'
];

/** 伺服器／表單允許的最終大小 */
export const IMAGE_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
/** 選檔時可接受的原始上限（會先壓縮再上傳） */
export const IMAGE_SOURCE_MAX_BYTES = 25 * 1024 * 1024;

const extensionForType = (type) => {
    switch (type) {
        case 'image/jpeg':
        case 'image/jpg':
            return 'jpg';
        case 'image/png':
            return 'png';
        case 'image/gif':
            return 'gif';
        case 'image/webp':
            return 'webp';
        default:
            return 'jpg';
    }
};

/**
 * @param {File} file
 * @param {number} [maxSizeBytes] 最終上傳上限（預設 2MB）；type 檢查不受此限
 * @param {{ checkSize?: boolean }} [options] checkSize=false 時只驗格式
 */
export const validateImageFile = (
    file,
    maxSizeBytes = IMAGE_UPLOAD_MAX_BYTES,
    options = {}
) => {
    const checkSize = options.checkSize !== false;

    if (!file?.type || !ALLOWED_TYPES.includes(file.type)) {
        return {
            valid: false,
            message: '不支援的圖片格式，請使用 JPG、PNG、GIF 或 WebP'
        };
    }

    if (checkSize && file.size > maxSizeBytes) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        const limitMB = (maxSizeBytes / 1024 / 1024).toFixed(0);
        return {
            valid: false,
            message: `圖片大小（${fileSizeMB}MB）超過 ${limitMB}MB 限制，請先壓縮圖片後再上傳`,
            suggestions: [
                '使用圖片壓縮工具（如 TinyPNG、Squoosh）',
                '調整圖片尺寸（建議寬度 1920px 以內）',
                '降低圖片品質（建議 80-85%）',
                '轉換為更高效的格式（如 WebP）'
            ]
        };
    }

    return { valid: true };
};

/**
 * 單次 canvas 壓縮
 * @param {File} file
 * @param {{ maxWidth?: number, maxHeight?: number, quality?: number, forceJpeg?: boolean }} opts
 */
export const compressImage = (
    file,
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.75
) => {
    // 相容舊呼叫 compressImage(file, w, h, q)
    const opts =
        typeof maxWidth === 'object' && maxWidth !== null
            ? maxWidth
            : { maxWidth, maxHeight, quality };

    const {
        maxWidth: w = 1920,
        maxHeight: h = 1920,
        quality: q = 0.75,
        forceJpeg = false
    } = opts;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const originalSize = file.size;

                if (width > w || height > h) {
                    const ratio = Math.min(w / width, h / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                let outputType = file.type;
                let outputQuality = q;

                // 大 PNG 或強制壓到 2MB 以下 → 改 JPEG 體積較小
                if (
                    forceJpeg ||
                    (file.type === 'image/png' && originalSize > 500 * 1024) ||
                    file.type === 'image/webp'
                ) {
                    if (file.type !== 'image/gif') {
                        outputType = 'image/jpeg';
                    }
                }

                if (outputType === 'image/png' && !forceJpeg) {
                    // PNG 無 quality，仍 try toBlob
                    outputQuality = undefined;
                }

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            resolve(file);
                            return;
                        }
                        if (blob.size >= originalSize && !forceJpeg) {
                            resolve(file);
                            return;
                        }
                        const baseName =
                            file.name.replace(/\.[^.]+$/, '') || 'image';
                        resolve(
                            new File(
                                [blob],
                                `${baseName}.${extensionForType(outputType)}`,
                                {
                                    type: outputType,
                                    lastModified: Date.now()
                                }
                            )
                        );
                    },
                    outputType,
                    outputType === 'image/png' ? undefined : outputQuality
                );
            };
            img.onerror = () => reject(new Error('圖片載入失敗'));
        };
        reader.onerror = () => reject(new Error('檔案讀取失敗'));
    });
};

/**
 * 反覆壓縮直到 ≤ maxBytes
 */
const compressUntilUnder = async (file, maxBytes = IMAGE_UPLOAD_MAX_BYTES) => {
    let current = file;
    const sizeSteps = [
        { maxWidth: 1920, maxHeight: 1920, quality: 0.82, forceJpeg: false },
        { maxWidth: 1600, maxHeight: 1600, quality: 0.75, forceJpeg: true },
        { maxWidth: 1280, maxHeight: 1280, quality: 0.68, forceJpeg: true },
        { maxWidth: 1024, maxHeight: 1024, quality: 0.6, forceJpeg: true },
        { maxWidth: 800, maxHeight: 800, quality: 0.55, forceJpeg: true },
        { maxWidth: 640, maxHeight: 640, quality: 0.5, forceJpeg: true }
    ];

    for (const step of sizeSteps) {
        if (current.size <= maxBytes) break;
        const next = await compressImage(current, step);
        // 若完全壓不動就繼續下一步（更小）
        if (next.size < current.size) {
            current = next;
        } else if (step.forceJpeg) {
            current = next;
        }
    }

    return current;
};

/**
 * 驗證並自動壓縮到 ≤2MB，回傳處理後的 File
 * @param {File} file
 * @param {{ message?: import('ant-design-vue').MessageApi }} options
 */
export const prepareImageForUpload = async (file, { message: msgApi } = {}) => {
    // 只驗格式；大小由後續壓縮處理
    const typeCheck = validateImageFile(file, IMAGE_UPLOAD_MAX_BYTES, {
        checkSize: false
    });
    if (!typeCheck.valid) {
        const err = new Error(typeCheck.message);
        err.validation = typeCheck;
        throw err;
    }

    if (file.size > IMAGE_SOURCE_MAX_BYTES) {
        const mb = (file.size / 1024 / 1024).toFixed(1);
        throw new Error(
            `圖片過大（${mb}MB），請先改用小於 25MB 的圖再上傳`
        );
    }

    // 已符合限制且不必縮尺寸
    if (file.size <= IMAGE_UPLOAD_MAX_BYTES) {
        return file;
    }

    const hide = msgApi?.loading?.(
        `圖片 ${(file.size / 1024 / 1024).toFixed(2)}MB，正在自動壓縮…`,
        0
    );

    try {
        const compressed = await compressUntilUnder(
            file,
            IMAGE_UPLOAD_MAX_BYTES
        );

        if (compressed.size > IMAGE_UPLOAD_MAX_BYTES) {
            throw new Error(
                `壓縮後仍約 ${(compressed.size / 1024 / 1024).toFixed(2)}MB（上限 2MB），請改用較簡單的圖`
            );
        }

        if (msgApi?.success && compressed.size < file.size) {
            const ratio = (
                ((file.size - compressed.size) / file.size) *
                100
            ).toFixed(0);
            msgApi.success(
                `已自動壓縮至 ${(compressed.size / 1024).toFixed(0)} KB（約減少 ${ratio}%）`
            );
        }

        return compressed;
    } finally {
        hide?.();
        msgApi?.destroy?.();
    }
};
